<script lang="ts" module>
	export type Segment<T extends string = string> = {
		/** Semantic identifier — exposed via bind:value. */
		value: T;
		/** Display label. */
		name: string;
		/** Pathname for the segment's link. Active state matches against page.url.pathname. */
		href: string;
	};
</script>

<script lang="ts" generics="T extends string">
	import type { ClassValue } from "svelte/elements";
	import Button from "./Button.svelte";

	type Props = {
		/** Label for the segmented control. Always rendered for a11y; visually shown only when `showLabel` is true. */
		label: string;
		/** When true, render `label` as a visible <p>. Otherwise it's screen-reader-only. */
		showLabel?: boolean;
		segments: Segment<T>[];
		/**
		 * Current pathname. Pass `page.url.pathname` from `$app/state` in
		 * a SvelteKit consumer. Kept as a prop so this component doesn't
		 * depend on SvelteKit and stays trivially testable in Storybook.
		 */
		currentPath: string;
		/** Active segment's value (bindable, derived from currentPath). */
		value?: T;
		/** Classes applied to the inner <ul> (the visible pill), not the outer <nav>. */
		class?: ClassValue;
		/** When true, segment links carry `data-sveltekit-noscroll` so switching
		 *  segments preserves the current scroll position instead of resetting
		 *  to the top. Useful for in-page tab-style toggles. */
		keepScroll?: boolean;
		/*
		 * Future expansion: an optional `segment` snippet for custom pill content
		 * (icons, badges). Add it here when needed — type signature:
		 *   segment?: Snippet<[Segment<T> & { active: boolean }]>
		 * Then render in the Button body with a fallback to `s.name`.
		 *
		 * Future expansion: a state-only variant (segments without href, rendered
		 * via Button without href + onclick that sets `value`). Refactor `href`
		 * to optional and branch on `s.href` in the {#each}.
		 */
	};

	let {
		label,
		showLabel = false,
		segments,
		currentPath,
		value = $bindable(),
		class: classes = "",
		keepScroll = false,
	}: Props = $props();

	const labelId = $props.id();

	const activeValue = $derived.by(() => {
		const path = currentPath.replace(/\/$/, "");
		const match = segments.find((s) => s.href.replace(/\/$/, "") === path);
		return match?.value ?? segments[0]?.value;
	});

	$effect(() => {
		if (value !== activeValue) value = activeValue;
	});
</script>

<nav aria-labelledby={labelId}>
	<p id={labelId} class={[!showLabel && "sr-only"]}>{label}</p>
	<ul class={[classes]}>
		{#each segments as s (s.value)}
			{@const isActive = activeValue === s.value}
			<li>
				<Button
					href={s.href}
					class={[
						"rounded-[.5rem] w-full min-w-0 px-1.5 not-focus:[--border-button:transparent] whitespace-nowrap",
						isActive && "drop-shadow-sm",
					]}
					link={!isActive}
					secondary={isActive}
					noScroll={keepScroll}
					aria-current={isActive ? "page" : undefined}
				>
					{s.name}
				</Button>
			</li>
		{/each}
	</ul>
</nav>

<style lang="postcss">
	ul {
		list-style: none;
		display: flex;

		& > li {
			flex: 1;
		}
	}

	@layer components {
		ul {
			background: hsl(from var(--lysning) h s l/0.1);
			border-radius: 0.5rem;
			padding: 0.1875rem;
		}
	}
</style>
